import { FormEvent, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Camera,
  Check,
  ChevronRight,
  Droplets,
  Gauge,
  ImagePlus,
  Leaf,
  Loader2,
  LogIn,
  RotateCcw,
  Search,
  ShieldAlert,
  Sprout,
  SunMedium,
  Thermometer,
  Wind,
  X,
} from 'lucide-react';
import { Link, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { AnalyseResponse, HealthAssessment, Plant, SavedPlant, SessionUser } from './types';
import { careGuidePath } from './plant-paths';
import './launch.css';
import './funfact.css';
import './plant-profile-photo.css';
import './scan-quality.css';

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: 'include', ...init });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || body.message || 'Request failed');
  return body as T;
}

const DIRECT_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const SOCIAL_IMAGE_PATH = '/social-share.png';
const SCAN_TIMEOUT_MS = 45_000;
const MAX_SCAN_PHOTOS = 3;

const FAQS = [
  {
    question: 'Is my plant photo private?',
    answer: 'A scan photo is not stored just because you identify a plant. If you save a plant on the Free plan, LetItGrow may retain a de-identified reference copy to improve identification accuracy; the account-linked photo remains private.',
  },
  {
    question: 'Does every plant lookup use AI?',
    answer: 'No. Name searches check the shared LetItGrow plant library first. AI is used for photo identification and when a plant care profile is not already available.',
  },
  {
    question: 'How quickly should a plant scan return?',
    answer: 'LetItGrow aims to return a plant check quickly on a normal connection. Multi-photo scans are stopped after 45 seconds rather than leaving you on an endless loading screen, and you can retry immediately.',
  },
  {
    question: 'Should I water exactly on the suggested schedule?',
    answer: 'No. The schedule is a rough rhythm. Always use the moisture-check guidance as well, because pot size, season, temperature and light can change how quickly soil dries.',
  },
];

type PhotoQuality = {
  score: number;
  warnings: string[];
  width: number;
  height: number;
};

type PreparedPhoto = {
  file: File;
  preview: string;
  quality: PhotoQuality;
};

type PendingScan = {
  photos: PreparedPhoto[];
  analysis: AnalyseResponse;
};

type PlantPhotoRouteState = {
  plantPhoto?: string;
  plantId?: string;
  photoSource?: 'scan' | 'saved' | 'community';
};

let pendingScan: PendingScan | null = null;

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function usePageMeta(title: string, description: string) {
  useEffect(() => {
    document.title = title;
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', window.location.href);
    setMeta('property', 'og:image', `${window.location.origin}${SOCIAL_IMAGE_PATH}`);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', `${window.location.origin}${SOCIAL_IMAGE_PATH}`);

    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${window.location.origin}${window.location.pathname}`;
  }, [title, description]);
}

function useJsonLd(id: string, value: unknown) {
  useEffect(() => {
    const scriptId = `jsonld-${id}`;
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.text = JSON.stringify(value);
    return () => script?.remove();
  }, [id, value]);
}

async function compressImage(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const maxSide = 1800;
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      bitmap.close();
      if (DIRECT_IMAGE_TYPES.has(file.type)) return file;
      throw new Error('This photo format could not be converted. Please choose a JPG, PNG or WebP image.');
    }
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (!blob) {
      if (DIRECT_IMAGE_TYPES.has(file.type)) return file;
      throw new Error('This photo format could not be converted. Please choose a JPG, PNG or WebP image.');
    }
    return new File([blob], `plant-${Date.now()}.jpg`, { type: 'image/jpeg' });
  } catch (error) {
    if (DIRECT_IMAGE_TYPES.has(file.type)) return file;
    if (error instanceof Error && error.message.includes('photo format')) throw error;
    throw new Error('Your phone returned a photo format this browser could not open. Please choose a JPG, PNG or WebP photo, or turn off HEIF/HEIC for the next camera photo.');
  }
}

async function assessImageQuality(file: File): Promise<PhotoQuality> {
  const bitmap = await createImageBitmap(file);
  const width = bitmap.width;
  const height = bitmap.height;
  const warnings: string[] = [];
  const blocking: string[] = [];

  if (Math.min(width, height) < 480) blocking.push('This photo is too small. Use a higher-resolution image or move closer to the plant.');

  const sampleSize = 96;
  const canvas = document.createElement('canvas');
  canvas.width = sampleSize;
  canvas.height = sampleSize;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    bitmap.close();
    return { score: 70, warnings: ['Photo quality could not be checked locally, so LetItGrow will verify it during identification.'], width, height };
  }
  context.drawImage(bitmap, 0, 0, sampleSize, sampleSize);
  bitmap.close();
  const pixels = context.getImageData(0, 0, sampleSize, sampleSize).data;
  const luminance = new Float32Array(sampleSize * sampleSize);
  let total = 0;
  for (let i = 0, p = 0; i < pixels.length; i += 4, p++) {
    const value = 0.2126 * pixels[i] + 0.7152 * pixels[i + 1] + 0.0722 * pixels[i + 2];
    luminance[p] = value;
    total += value;
  }
  const mean = total / luminance.length;
  let variance = 0;
  let gradient = 0;
  let gradientCount = 0;
  for (let y = 0; y < sampleSize; y++) {
    for (let x = 0; x < sampleSize; x++) {
      const index = y * sampleSize + x;
      const delta = luminance[index] - mean;
      variance += delta * delta;
      if (x > 0) { gradient += Math.abs(luminance[index] - luminance[index - 1]); gradientCount++; }
      if (y > 0) { gradient += Math.abs(luminance[index] - luminance[index - sampleSize]); gradientCount++; }
    }
  }
  const contrast = Math.sqrt(variance / luminance.length);
  const sharpness = gradientCount ? gradient / gradientCount : 0;

  if (mean < 28) blocking.push('This photo is too dark. Add light or move somewhere brighter before scanning.');
  if (mean > 238) blocking.push('This photo is heavily overexposed. Reduce glare or direct light before scanning.');
  if (contrast < 14) warnings.push('The plant has very little visible contrast. A closer angle with clearer leaf edges may improve accuracy.');
  if (sharpness < 5.5) warnings.push('This photo looks soft or slightly blurry. Hold the phone steady and tap the plant to focus.');
  if (sharpness < 3.5 && contrast < 10) blocking.push('This photo is too blurry to identify reliably. Retake it with the leaves or flower in focus.');

  if (blocking.length) throw new Error(blocking.join(' '));
  const score = Math.max(25, Math.min(100, Math.round(100 - warnings.length * 18 - (contrast < 20 ? 8 : 0) - (sharpness < 8 ? 8 : 0))));
  return { score, warnings, width, height };
}

function formatSetting(value: string) {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDate(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
}

function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    api<{ user: SessionUser | null }>('/api/auth/me').then((r) => setUser(r.user)).catch(() => setUser(null));
  }, []);

  return (
    <div className="app-shell">
      <Header
        user={user}
        onAuth={() => setAuthOpen(true)}
        onLogout={async () => {
          try {
            await api('/api/auth/logout', { method: 'POST' });
          } finally {
            setUser(null);
          }
        }}
      />
      <Routes>
        <Route path="/" element={<Home user={user} onNeedAuth={() => setAuthOpen(true)} />} />
        <Route path="/plants/:id" element={<PlantProfile />} />
        <Route path="/my-plants" element={<MyPlants user={user} onNeedAuth={() => setAuthOpen(true)} />} />
        <Route path="/my-plants/:id" element={<SavedPlantProfile user={user} onNeedAuth={() => setAuthOpen(true)} />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {authOpen && (
        <AuthModal
          onClose={() => setAuthOpen(false)}
          onAuthenticated={(next) => {
            setUser(next);
            setAuthOpen(false);
          }}
        />
      )}
    </div>
  );
}

function Header({ user, onAuth, onLogout }: { user: SessionUser | null; onAuth: () => void; onLogout: () => void }) {
  return (
    <header className="topbar">
      <Link to="/" className="brand" aria-label="LetItGrow home">
        <span className="brand-mark"><Leaf size={20} /></span>
        <span>LetItGrow</span>
      </Link>
      <nav aria-label="Main navigation">
        <Link to="/">Discover</Link>
        <Link to="/my-plants">My plants</Link>
        {user ? (
          <button type="button" className="link-button" onClick={onLogout}>Sign out</button>
        ) : (
          <button type="button" className="pill small" onClick={onAuth}><LogIn size={16} /> Sign in</button>
        )}
      </nav>
    </header>
  );
}

function Home({ user, onNeedAuth }: { user: SessionUser | null; onNeedAuth: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const appendModeRef = useRef(false);
  const scanFeedbackRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Plant[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [searchMessage, setSearchMessage] = useState('');
  const [photos, setPhotos] = useState<PreparedPhoto[]>(() => pendingScan?.photos ?? []);
  const [analysis, setAnalysis] = useState<AnalyseResponse | null>(() => pendingScan?.analysis ?? null);
  const [preparing, setPreparing] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const preview = photos[0]?.preview ?? null;
  const image = photos[0]?.file ?? null;

  usePageMeta(
    'LetItGrow | Identify plants and learn how to care for them',
    'Take one to three plant photos for confidence-aware identification, practical care guidance, a visual health check, and a private saved plant shelf.',
  );
  useJsonLd('faq', {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  });

  function revealScanFeedback() {
    window.setTimeout(() => {
      scanFeedbackRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  }

  function revokePhotos(items: PreparedPhoto[]) {
    for (const photo of items) URL.revokeObjectURL(photo.preview);
  }

  function startPicker(append: boolean) {
    if (preparing || analysing) return;
    appendModeRef.current = append;
    inputRef.current?.click();
  }

  useEffect(() => {
    if (pendingScan) revealScanFeedback();
  }, []);

  async function onSearch(event: FormEvent) {
    event.preventDefault();
    const value = query.trim();
    if (value.length < 2) {
      setSearchAttempted(true);
      setSearchMessage('Type at least two letters to search the plant library.');
      return;
    }
    setSearching(true);
    setSearchAttempted(true);
    setSearchMessage('');
    setError('');
    setAnalysis(null);
    try {
      const data = await api<{ results: Plant[]; message?: string }>('/api/plants/search?q=' + encodeURIComponent(value));
      setResults(data.results);
      setSearchMessage(data.message || (data.results.length === 0 ? 'No plant matched that name. Try a common name or scientific name.' : ''));
    } catch (e) {
      setResults([]);
      setError(e instanceof Error ? e.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  }

  async function selectFiles(fileList: FileList | null, append: boolean) {
    if (!fileList?.length || preparing || analysing) return;
    setPreparing(true);
    setError('');
    setSaved(false);
    setResults([]);
    setSearchAttempted(false);

    const base = append ? photos : [];
    if (!append) {
      revokePhotos(photos);
      pendingScan = null;
    }
    const available = Math.max(0, MAX_SCAN_PHOTOS - base.length);
    const selected = Array.from(fileList).slice(0, available);
    const prepared: PreparedPhoto[] = [];
    const rejected: string[] = [];

    try {
      for (const file of selected) {
        try {
          const quality = await assessImageQuality(file);
          const compressed = await compressImage(file);
          if (!DIRECT_IMAGE_TYPES.has(compressed.type)) throw new Error('Please choose a JPG, PNG or WebP image.');
          if (compressed.size > 7 * 1024 * 1024) throw new Error('That image is still too large. Please choose a photo smaller than 7 MB.');
          prepared.push({ file: compressed, preview: URL.createObjectURL(compressed), quality });
        } catch (e) {
          rejected.push(e instanceof Error ? e.message : 'One photo could not be prepared.');
        }
      }
      const next = [...base, ...prepared].slice(0, MAX_SCAN_PHOTOS);
      setPhotos(next);
      setAnalysis(null);
      if (rejected.length) setError(rejected.join(' '));
      if (fileList.length > available) setError(`Use up to ${MAX_SCAN_PHOTOS} photos of the same plant.`);
      revealScanFeedback();
    } finally {
      setPreparing(false);
    }
  }

  function removePhoto(index: number) {
    if (analysing) return;
    setPhotos((current) => {
      const removed = current[index];
      if (removed) URL.revokeObjectURL(removed.preview);
      return current.filter((_, itemIndex) => itemIndex !== index);
    });
    pendingScan = null;
    setAnalysis(null);
    setSaved(false);
  }

  function resetCapture() {
    revokePhotos(photos);
    setPhotos([]);
    setAnalysis(null);
    setSaved(false);
    setError('');
    pendingScan = null;
  }

  async function analysePhotos() {
    if (photos.length === 0 || analysing || preparing) return;
    setAnalysing(true);
    setError('');
    setSaved(false);
    revealScanFeedback();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), SCAN_TIMEOUT_MS);
    try {
      const form = new FormData();
      photos.forEach((photo) => form.append('images', photo.file));
      const nextAnalysis = await api<AnalyseResponse>('/api/analyse', { method: 'POST', body: form, signal: controller.signal });
      setAnalysis(nextAnalysis);
      pendingScan = { photos, analysis: nextAnalysis };
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        setError('This plant check took longer than 45 seconds, so it was stopped. Please retry or use fewer, clearer photos.');
      } else {
        setError(e instanceof Error ? e.message : 'Could not analyse these photos');
      }
    } finally {
      window.clearTimeout(timeout);
      setAnalysing(false);
      revealScanFeedback();
    }
  }

  async function savePlant() {
    if (!analysis?.plant || !image || saving) return;
    if (!user) {
      onNeedAuth();
      return;
    }
    const form = new FormData();
    form.append('plantId', analysis.plant.id);
    form.append('image', image);
    form.append('assessment', JSON.stringify(analysis.assessment));
    setSaving(true);
    setError('');
    try {
      await api('/api/my-plants', { method: 'POST', body: form });
      pendingScan = null;
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save plant');
    } finally {
      setSaving(false);
    }
  }

  const labels = ['Whole plant', 'Leaf / stem detail', 'Flower / fruit detail'];
  const allWarnings = photos.flatMap((photo) => photo.quality.warnings);

  return (
    <main>
      <section className="hero">
        <div className="hero-leaf hero-leaf-one" aria-hidden="true" />
        <div className="hero-leaf hero-leaf-two" aria-hidden="true" />
        <div className="eyebrow"><Sprout size={16} /> A calmer way to care for plants</div>
        <h1>Meet your plant.<br />Help it thrive.</h1>
        <p>Take a clear photo to identify your plant. For difficult plants, add a close leaf or flower view so the two verification passes have more botanical evidence.</p>
        <div className="hero-actions">
          <button type="button" className="pill primary" disabled={analysing || preparing} onClick={() => startPicker(false)}>
            {preparing ? <><Loader2 className="spin" size={19} /> Checking photo quality...</> : analysing ? <><Loader2 className="spin" size={19} /> Analysing your plant...</> : <><Camera size={19} /> Take or choose plant photos</>}
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            hidden
            onChange={(e) => {
              void selectFiles(e.target.files, appendModeRef.current);
              e.currentTarget.value = '';
            }}
          />
          <span className="privacy-note"><Leaf size={13} /> Scan photos are transient. Saved Free-plan photos may create a de-identified accuracy reference copy; account photos stay private.</span>
          <span className="response-promise"><Gauge size={15} /> Response-time promise: no endless spinner — multi-photo scans stop after 45 seconds and show a clear retry.</span>
        </div>
      </section>

      <section className="workspace">
        <div className="section-intro">
          <div><span className="eyebrow">Shared plant library</span><h2>Already know its name?</h2></div>
          <p>Search first. LetItGrow reuses existing care profiles whenever possible.</p>
        </div>
        <form className="search-box" onSubmit={onSearch}>
          <Search size={19} aria-hidden="true" />
          <input
            value={query}
            maxLength={120}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Monstera, pothos, snake plant..."
            aria-label="Search plants"
          />
          <button disabled={searching}>{searching ? <Loader2 className="spin" size={18} /> : 'Search'}</button>
        </form>

        <div ref={scanFeedbackRef} className="scan-feedback-anchor" aria-hidden="true" />
        {error && <div className="error-card" role="alert">{error}</div>}

        {photos.length > 0 && !analysing && (
          <section className="accuracy-capture" aria-label="Photos selected for plant identification">
            <div className="accuracy-capture-head">
              <div><span className="eyebrow">Accuracy-first scan</span><h3>{photos.length === 1 ? 'One clear photo can work. More views can help.' : `${photos.length} views ready for verification`}</h3></div>
              <button type="button" className="capture-reset" onClick={resetCapture}><RotateCcw size={15} /> Start over</button>
            </div>
            <p className="accuracy-copy">Use photos of the <strong>same plant</strong>. A whole-plant view gives growth habit; a leaf/stem close-up and flower or fruit can separate lookalike species.</p>
            <div className="capture-photo-grid">
              {photos.map((photo, index) => (
                <article className="capture-photo" key={photo.preview}>
                  <div className="capture-photo-image"><img src={photo.preview} alt={`${labels[index]} selected for identification`} /><button type="button" onClick={() => removePhoto(index)} aria-label={`Remove ${labels[index]}`}><X size={16} /></button></div>
                  <div><strong>{labels[index]}</strong><span className={photo.quality.warnings.length ? 'quality-warn' : 'quality-good'}>{photo.quality.warnings.length ? 'Usable · could be clearer' : 'Good photo quality'}</span><small>{photo.quality.width} × {photo.quality.height}</small></div>
                </article>
              ))}
              {photos.length < MAX_SCAN_PHOTOS && (
                <button type="button" className="capture-add" onClick={() => startPicker(true)} disabled={preparing}><ImagePlus size={24} /><strong>Add supporting view</strong><span>{labels[photos.length]}</span></button>
              )}
            </div>
            {allWarnings.length > 0 && <div className="quality-advice"><strong>Photo tip</strong><p>{Array.from(new Set(allWarnings)).join(' ')}</p></div>}
            <div className="capture-actions">
              <button type="button" className="pill primary" onClick={() => void analysePhotos()} disabled={preparing}>{preparing ? <><Loader2 className="spin" size={17} /> Preparing...</> : <><Sprout size={17} /> Identify with {photos.length} photo{photos.length === 1 ? '' : 's'}</>}</button>
              <span>{photos.length === 1 ? 'You can scan now or add another angle.' : 'All photos are checked together, then independently verified.'}</span>
            </div>
          </section>
        )}

        {analysing && (
          <div className="analysis-loading" aria-live="polite">
            {preview && <img src={preview} alt="Plant selected for identification" />}
            <div><Loader2 className="spin" size={28} /><h3>Comparing botanical details across {photos.length} photo{photos.length === 1 ? '' : 's'}</h3><p>Two independent identification passes are checking the same evidence before LetItGrow accepts a match.</p></div>
          </div>
        )}

        {analysis && !analysing && <AnalysisCard data={analysis} preview={preview} saved={saved} saving={saving} onSave={savePlant} userPlan={user?.plan} />}

        {!analysis && results.length > 0 && photos.length === 0 && (
          <div className="results-area">
            <div className="results-label">{results.length === 1 ? 'Plant match' : `${results.length} plant matches`}</div>
            <div className="results-grid">{results.map((plant) => <PlantCard key={plant.id} plant={plant} />)}</div>
          </div>
        )}

        {!analysis && !analysing && searchAttempted && results.length === 0 && photos.length === 0 && !error && (
          <div className="search-empty"><Sprout size={34} /><h3>Nothing growing here yet</h3><p>{searchMessage}</p></div>
        )}

        {!analysis && !analysing && !searchAttempted && results.length === 0 && photos.length === 0 && (
          <div className="feature-strip">
            <article><Camera /><h3>Identify from botanical evidence</h3><p>Start with the whole plant and add leaf, stem, flower or fruit views when you can.</p></article>
            <article><Droplets /><h3>Water with confidence</h3><p>See the usual rhythm and, more importantly, how to check the soil before watering.</p></article>
            <article><SunMedium /><h3>Find its happy place</h3><p>Understand indoor, outdoor, direct and indirect light needs in plain language.</p></article>
          </div>
        )}
      </section>

      <section className="faq-section" aria-labelledby="faq-title">
        <div className="faq-head">
          <span className="eyebrow">Plant care, explained</span>
          <h2 id="faq-title">A few things worth knowing</h2>
          <p>LetItGrow is designed to keep the useful parts simple: scan, understand, care, and save only when you want to.</p>
        </div>
        <div className="faq-grid">
          {FAQS.map((item) => <article className="faq-card" key={item.question}><h3>{item.question}</h3><p>{item.answer}</p></article>)}
        </div>
      </section>

      <button
        type="button"
        className="mobile-scan-cta"
        disabled={analysing || preparing}
        onClick={() => {
          if (photos.length > 0 && !analysis) void analysePhotos();
          else startPicker(false);
        }}
      >
        {preparing ? <><Loader2 className="spin" size={18} /> Checking photo...</> : analysing ? <><Loader2 className="spin" size={18} /> Analysing plant...</> : photos.length > 0 && !analysis ? <><Sprout size={18} /> Identify with {photos.length} photo{photos.length === 1 ? '' : 's'}</> : <><Camera size={18} /> {analysis ? 'Scan another plant' : 'Scan a plant'}</>}
      </button>
    </main>
  );
}

function AnalysisCard({ data, preview, onSave, saved, saving, userPlan }: { data: AnalyseResponse; preview: string | null; onSave: () => void; saved: boolean; saving: boolean; userPlan?: 'free' | 'pro' }) {
  if (!data.plant) return <div className="result-panel gentle-empty"><Sprout size={34} /><h2>Try another angle</h2><p>{data.message || 'The plant is not clear enough to identify confidently. Try a brighter photo that shows the leaves and stem.'}</p></div>;
  const p = data.plant;
  return (
    <div className="result-panel">
      <div className="result-hero">
        {preview && <img src={preview} alt={p.common_name} />}
        <div>
          <div className="eyebrow">{data.cached_profile ? 'Found in the LetItGrow library' : 'Added to the LetItGrow library'}{data.image_count && data.image_count > 1 ? ` · ${data.image_count} photos checked` : ''}</div>
          <h2>{p.common_name}</h2>
          <em>{p.scientific_name}</em>
          <p>{p.description}</p>
          <FunFact text={p.fun_fact} compact />
          <div className="result-actions">
            <button type="button" className="pill primary" onClick={onSave} disabled={saved || saving}>
              {saving ? <><Loader2 className="spin" size={17} /> Saving...</> : saved ? <><Check size={17} /> Saved to My plants</> : 'Save to My plants'}
            </button>
            <Link
              className="text-link"
              to={careGuidePath(p)}
              state={preview ? { plantPhoto: preview, photoSource: 'scan' } satisfies PlantPhotoRouteState : undefined}
            >
              Open full care guide <ChevronRight size={16} />
            </Link>
          </div>
          {userPlan !== 'pro' && <p className="save-reference-note">Free-plan saved photos may also create a de-identified reference copy used to improve identification accuracy. Your account-linked plant photo stays private. <Link to="/privacy">Privacy details</Link></p>}
        </div>
      </div>
      <div className="care-grid">
        <Care icon={<SunMedium />} title="Light" value={p.light} />
        <Care icon={<Droplets />} title="Water" value={p.watering_frequency} detail={p.watering_check} />
        <Care icon={<Leaf />} title="Best setting" value={formatSetting(p.indoor_outdoor)} />
      </div>
      <HealthCard assessment={data.assessment} confidence={data.identification_confidence} />
    </div>
  );
}

function FunFact({ text, compact = false }: { text?: string | null; compact?: boolean }) {
  if (!text) return null;
  return (
    <aside className={`fun-fact${compact ? ' compact' : ''}`}>
      <span className="fun-fact-mark" aria-hidden="true">✦</span>
      <div><strong>Did you know?</strong><p>{text}</p></div>
    </aside>
  );
}

function HealthCard({ assessment, confidence, date }: { assessment: HealthAssessment; confidence?: number; date?: string | null }) {
  return (
    <div className={'health-card ' + assessment.health_status}>
      <div>
        <span className="status-dot" />
        <strong>{formatSetting(assessment.health_status)}</strong>
        {typeof confidence === 'number' && <span>{Math.round(confidence * 100)}% ID confidence</span>}
        {date && <span>{formatDate(date)}</span>}
      </div>
      <h3>How this plant looks right now</h3>
      <p>{assessment.health_summary}</p>
      {assessment.visible_issues.length > 0 && <ul>{assessment.visible_issues.map((item) => <li key={item}>{item}</li>)}</ul>}
      {assessment.recommendations.length > 0 && <div className="recommendations">{assessment.recommendations.map((item) => <span key={item}>{item}</span>)}</div>}
    </div>
  );
}

function Care({ icon, title, value, detail }: { icon: ReactNode; title: string; value: string; detail?: string | null }) {
  return <article className="care-card"><span>{icon}</span><div><small>{title}</small><strong>{value}</strong>{detail && <p>{detail}</p>}</div></article>;
}

function PlantCard({ plant }: { plant: Plant }) {
  return (
    <Link className="plant-card" to={careGuidePath(plant)}>
      <div className="plant-icon"><Leaf /></div>
      <div><h3>{plant.common_name}</h3><em>{plant.scientific_name}</em><p>{plant.light}</p></div>
      <ChevronRight />
    </Link>
  );
}

function Breadcrumbs({ items }: { items: Array<{ label: string; to?: string }> }) {
  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <ol>{items.map((item, index) => <li key={`${item.label}-${index}`}>{item.to ? <Link to={item.to}>{item.label}</Link> : <span aria-current="page">{item.label}</span>}</li>)}</ol>
    </nav>
  );
}

function PlantProfile() {
  const { id } = useParams();
  const location = useLocation();
  const [plant, setPlant] = useState<Plant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const photoState = location.state as PlantPhotoRouteState | null;
  const routedPhoto = photoState && photoState.plantId === id ? (photoState.plantPhoto ?? null) : null;
  const routedSource = photoState && photoState.plantId === id ? photoState.photoSource : undefined;
  const pendingPhoto = pendingScan && pendingScan.analysis.plant?.id === id ? pendingScan.photos[0]?.preview || null : null;
  const profilePhoto = routedPhoto || pendingPhoto;
  const photoLabel = routedPhoto
    ? routedSource === 'saved' ? 'Your saved photo' : routedSource === 'community' ? 'Community photo' : 'Your scan'
    : pendingPhoto
      ? 'Your scan'
      : null;

  usePageMeta(
    plant ? `${plant.common_name} care guide | LetItGrow` : 'Plant care guide | LetItGrow',
    plant ? `Light, watering, soil, humidity, temperature and practical care guidance for ${plant.common_name} (${plant.scientific_name}).` : 'Open a practical LetItGrow plant care guide.',
  );

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    api<{ plant: Plant }>(`/api/plants/${encodeURIComponent(id)}`)
      .then((response) => setPlant(response.plant))
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load this plant'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <PageLoader label="Opening plant guide" />;
  if (error || !plant) return <ErrorState title="Plant guide unavailable" message={error || 'This plant could not be found.'} />;

  return (
    <main className="profile-page narrow">
      <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Plant guide' }, { label: plant.common_name }]} />
      <section className="profile-hero botanical-panel">
        <div className="profile-visual">
          {profilePhoto ? (
            <img src={profilePhoto} alt={`${plant.common_name} reference`} />
          ) : (
            <div className="profile-icon"><Leaf size={42} /></div>
          )}
          {profilePhoto && photoLabel && <span className="profile-photo-badge">{photoLabel}</span>}
        </div>
        <div>
          <span className="eyebrow">Shared plant profile</span>
          <h1>{plant.common_name}</h1>
          <em>{plant.scientific_name}</em>
          {plant.family && <span className="family-chip">{plant.family}</span>}
          <p>{plant.description}</p>
          <FunFact text={plant.fun_fact} />
        </div>
      </section>
      <FullCareGrid plant={plant} />
      <div className="care-note"><Sprout size={18} /><p>Care guidance is a starting point. Check the plant and soil before acting, because light, pot size, season and indoor climate can change what it needs.</p></div>
    </main>
  );
}

function FullCareGrid({ plant }: { plant: Plant }) {
  const cards = [
    { icon: <SunMedium />, title: 'Light', value: plant.light },
    { icon: <Droplets />, title: 'Watering rhythm', value: plant.watering_frequency, detail: plant.watering_check },
    { icon: <Leaf />, title: 'Best setting', value: formatSetting(plant.indoor_outdoor) },
    { icon: <Sprout />, title: 'Soil', value: plant.soil || 'Use a well-draining mix suited to the plant.' },
    { icon: <Wind />, title: 'Humidity', value: plant.humidity || 'Normal household humidity may be suitable.' },
    { icon: <Thermometer />, title: 'Temperature', value: plant.temperature || 'Protect from temperature extremes.' },
    { icon: <ShieldAlert />, title: 'Toxicity', value: plant.toxicity || 'Toxicity information is not available yet.' },
    { icon: <Gauge />, title: 'Care level', value: plant.care_difficulty || 'Not rated yet.' },
  ];
  return <section className="full-care-grid">{cards.map((card) => <Care key={card.title} {...card} />)}</section>;
}

function MyPlants({ user, onNeedAuth }: { user: SessionUser | null; onNeedAuth: () => void }) {
  const [items, setItems] = useState<SavedPlant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  usePageMeta('My plants | LetItGrow', 'Open your private LetItGrow plant shelf, saved photos and latest plant health notes.');

  useEffect(() => {
    if (!user) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    api<{ plants: SavedPlant[] }>('/api/my-plants')
      .then((r) => setItems(r.plants))
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load your plants'))
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) return <main className="narrow empty-state"><Leaf size={42} /><h1>Your private plant shelf</h1><p>Sign in to keep your saved plants, photos and health notes together. Shared care profiles remain searchable by everyone.</p><button type="button" className="pill primary" onClick={onNeedAuth}>Sign in to continue</button></main>;
  if (loading) return <PageLoader label="Opening your plant shelf" />;
  if (error) return <ErrorState title="Could not open My plants" message={error} />;

  return (
    <main className="narrow">
      <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'My plants' }]} />
      <div className="page-heading">
        <div><span className="eyebrow">Private collection</span><h1>My plants</h1><p>Everything here belongs to your account.</p></div>
        <button type="button" className="pill primary" onClick={() => navigate('/')}><Camera size={17} /> Add plant</button>
      </div>
      {items.length === 0 ? (
        <div className="empty-state shelf-empty"><Sprout size={44} /><h2>No saved plants yet</h2><p>Identify a plant and save it to start your collection.</p><button type="button" className="pill" onClick={() => navigate('/')}>Find my first plant</button></div>
      ) : (
        <div className="saved-grid">
          {items.map((item) => (
            <Link className="saved-card" to={`/my-plants/${item.id}`} key={item.id}>
              <div className="saved-media">
                {item.latest_checkin?.photo_url ? <img src={item.latest_checkin.photo_url} alt={item.plant.common_name} /> : <div className="saved-placeholder"><Leaf /></div>}
                <span className="private-badge">Private</span>
              </div>
              <div>
                <small>{item.nickname ? 'Your plant' : 'Saved plant'}</small>
                <h3>{item.nickname || item.plant.common_name}</h3>
                {item.nickname && <p className="species-line">{item.plant.common_name}</p>}
                <em>{item.plant.scientific_name}</em>
                {item.latest_checkin && <p className={'status-text ' + item.latest_checkin.health_status}>{formatSetting(item.latest_checkin.health_status)} · {item.latest_checkin.health_summary}</p>}
                <div className="saved-care"><span><SunMedium size={15} /> {item.plant.light}</span><span><Droplets size={15} /> {item.plant.watering_frequency}</span></div>
                <span className="open-card">Open plant <ChevronRight size={15} /></span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}

function SavedPlantProfile({ user, onNeedAuth }: { user: SessionUser | null; onNeedAuth: () => void }) {
  const { id } = useParams();
  const [saved, setSaved] = useState<SavedPlant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  usePageMeta(
    saved ? `${saved.nickname || saved.plant.common_name} | My plants | LetItGrow` : 'Saved plant | LetItGrow',
    saved ? `Private saved plant care and the latest health check for ${saved.nickname || saved.plant.common_name}.` : 'Open a private saved plant in LetItGrow.',
  );

  useEffect(() => {
    if (!user || !id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    api<{ saved: SavedPlant }>(`/api/my-plants/${encodeURIComponent(id)}`)
      .then(async (response) => {
        let next = response.saved;
        try {
          const publicProfile = await api<{ plant: Plant }>(`/api/plants/${encodeURIComponent(response.saved.plant.id)}`);
          next = { ...response.saved, plant: publicProfile.plant };
        } catch {
          // The private plant can still open if optional fun-fact enrichment is unavailable.
        }
        setSaved(next);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load this saved plant'))
      .finally(() => setLoading(false));
  }, [id, user]);

  if (!user) return <main className="narrow empty-state"><Leaf size={42} /><h1>This plant is private</h1><p>Sign in to open saved plant photos and health notes.</p><button type="button" className="pill primary" onClick={onNeedAuth}>Sign in to continue</button></main>;
  if (loading) return <PageLoader label="Opening your plant" />;
  if (error || !saved) return <ErrorState title="Saved plant unavailable" message={error || 'This saved plant could not be found.'} />;

  const plant = saved.plant;
  return (
    <main className="profile-page narrow">
      <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'My plants', to: '/my-plants' }, { label: saved.nickname || plant.common_name }]} />
      <section className="saved-profile-hero botanical-panel">
        <div className="saved-profile-photo">
          {saved.latest_checkin?.photo_url ? <img src={saved.latest_checkin.photo_url} alt={plant.common_name} /> : <div className="saved-placeholder"><Leaf size={40} /></div>}
          <span className="private-badge">Private</span>
        </div>
        <div>
          <span className="eyebrow">Saved plant · private profile</span>
          <h1>{saved.nickname || plant.common_name}</h1>
          {saved.nickname && <h2>{plant.common_name}</h2>}
          <em>{plant.scientific_name}</em>
          <p>{plant.description}</p>
          <FunFact text={plant.fun_fact} />
          <div className="profile-meta"><span>Saved {formatDate(saved.created_at)}</span>{saved.last_checked_at && <span>Last checked {formatDate(saved.last_checked_at)}</span>}</div>
        </div>
      </section>

      {saved.latest_checkin && <HealthCard assessment={saved.latest_checkin} date={saved.latest_checkin.created_at} />}
      <div className="section-intro profile-care-heading">
        <div><span className="eyebrow">Care guide</span><h2>What it needs</h2></div>
        <Link
          className="text-link"
          to={careGuidePath(plant)}
          state={saved.latest_checkin?.photo_url ? { plantPhoto: saved.latest_checkin.photo_url, photoSource: 'saved' } satisfies PlantPhotoRouteState : undefined}
        >
          View public species profile <ChevronRight size={16} />
        </Link>
      </div>
      <FullCareGrid plant={plant} />
    </main>
  );
}

function AuthModal({ onClose, onAuthenticated }: { onClose: () => void; onAuthenticated: (u: SessionUser) => void }) {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const title = useMemo(() => mode === 'login' ? 'Welcome back' : 'Create your plant shelf', [mode]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await api<{ user: SessionUser }>(`/api/auth/${mode}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      onAuthenticated(result.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="auth-title" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close sign in"><X /></button>
        <span className="brand-mark"><Leaf /></span>
        <h2 id="auth-title">{title}</h2>
        <p>{mode === 'login' ? 'Open your private plants, photos and care notes.' : 'Save plants privately while reusing the shared care library.'}</p>
        <form onSubmit={submit}>
          <label>Email<input autoFocus type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
          <label>Password<input type="password" minLength={10} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
          {error && <div className="form-error" role="alert">{error}</div>}
          <button className="pill primary wide" disabled={busy}>{busy ? <Loader2 className="spin" /> : mode === 'login' ? 'Sign in' : 'Create account'}</button>
        </form>
        <button type="button" className="link-button switch" onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); }}>
          {mode === 'login' ? 'New here? Create an account' : 'Already have an account? Sign in'}
        </button>
      </div>
    </div>
  );
}

function NotFound() {
  usePageMeta('Page not found | LetItGrow', 'This LetItGrow page could not be found. Return home to identify a plant or open your private plant shelf.');
  return (
    <main className="not-found">
      <section className="not-found-card">
        <Sprout size={48} />
        <span className="eyebrow">404 · nothing planted here</span>
        <h1>This path didn’t grow.</h1>
        <p>The page may have moved, or the link may have been mistyped. Your plant shelf and the scanner are still safe.</p>
        <div className="not-found-actions"><Link className="pill primary" to="/">Identify a plant</Link><Link className="pill" to="/my-plants">My plants</Link></div>
      </section>
    </main>
  );
}

function PageLoader({ label }: { label: string }) {
  return <main className="narrow empty-state page-loader" aria-live="polite"><Loader2 className="spin" size={30} /><p>{label}...</p></main>;
}

function ErrorState({ title, message }: { title: string; message: string }) {
  return <main className="narrow empty-state"><Sprout size={42} /><h1>{title}</h1><p>{message}</p><Link className="pill" to="/">Back to LetItGrow</Link></main>;
}

export default App;
